import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowCounterClockwise, PencilSimple, Check, X } from "@phosphor-icons/react";

// Format value based on unit type
function formatValue(value, unit) {
    if (!unit) return value;
    if (unit === "%") return `${(Number(value) * 100).toFixed(2)}%`;
    if (unit === "$ pa") return `$${Number(value).toLocaleString("en-AU")}`;
    if (unit === "Age" || unit === "FY") return Number(value);
    return value;
}

function parseValue(raw, unit) {
    if (raw === "" || raw === null) return 0;
    if (unit === "%") return Number(raw) / 100;
    // strip commas / dollar signs
    return Number(String(raw).replace(/[$,\s]/g, ""));
}

function toEditableString(value, unit) {
    if (unit === "%") return (Number(value) * 100).toFixed(2);
    return Number(value);
}

export default function Assumptions() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // { id, value, notes }
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/assumptions");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const startEdit = (a) => {
        setEditing({
            id: a.id,
            value: toEditableString(a.value, a.unit),
            notes: a.notes || "",
        });
    };

    const cancel = () => setEditing(null);

    const save = async (a) => {
        try {
            await api.put(`/assumptions/${a.id}`, {
                item: a.item,
                value: parseValue(editing.value, a.unit),
                unit: a.unit || "",
                notes: editing.notes,
            });
            toast.success(`${a.item} updated`);
            setEditing(null);
            await load();
        } catch {
            toast.error("Failed to update assumption");
        }
    };

    const resetAll = async () => {
        if (!confirm("Reset all assumptions to baseline defaults? Custom values will be lost.")) return;
        setBusy(true);
        try {
            await api.post("/assumptions/reset");
            toast.success("Reset to baseline defaults");
            setEditing(null);
            await load();
        } catch {
            toast.error("Reset failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="assumptions-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Planning Assumptions</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">
                        Numbers behind the plan.
                    </h1>
                    <p className="text-[var(--muted)] mt-3 max-w-2xl text-sm">
                        These drive the retirement projections, property models and AI
                        insights. Change them, then re-run the relevant scenario to see
                        the impact.
                    </p>
                </div>
                <button className="btn-ghost flex items-center gap-2 text-sm"
                        onClick={resetAll} disabled={busy}
                        data-testid="reset-assumptions-btn">
                    <ArrowCounterClockwise size={14} /> Reset to baseline
                </button>
            </div>

            <div className="hairline bg-white rounded overflow-hidden">
                <table className="editorial w-full" data-testid="assumptions-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th className="num">Value</th>
                            <th>Unit</th>
                            <th>Notes</th>
                            <th style={{ width: 96 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(a => {
                            const isEditing = editing?.id === a.id;
                            return (
                                <tr key={a.id} data-testid={`assumption-row-${a.item.replace(/\s+/g, "-").toLowerCase()}`}>
                                    <td className="font-medium">{a.item}</td>
                                    <td className="num font-mono-num">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                step="any"
                                                autoFocus
                                                value={editing.value}
                                                onChange={e => setEditing({ ...editing, value: e.target.value })}
                                                className="border border-[var(--line)] px-2 py-1 rounded text-sm w-24 text-right font-mono-num"
                                                data-testid={`assumption-input-${a.id}`}
                                            />
                                        ) : (
                                            formatValue(a.value, a.unit)
                                        )}
                                    </td>
                                    <td className="text-[var(--muted)] text-sm">{a.unit}</td>
                                    <td className="text-[var(--muted)] text-sm">
                                        {isEditing ? (
                                            <input
                                                value={editing.notes}
                                                onChange={e => setEditing({ ...editing, notes: e.target.value })}
                                                className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full"
                                            />
                                        ) : (
                                            a.notes
                                        )}
                                    </td>
                                    <td>
                                        {isEditing ? (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => save(a)}
                                                    className="text-[var(--eucalyptus)] hover:opacity-70"
                                                    data-testid={`assumption-save-${a.id}`}>
                                                    <Check size={16} />
                                                </button>
                                                <button
                                                    onClick={cancel}
                                                    className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => startEdit(a)}
                                                className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                data-testid={`assumption-edit-${a.id}`}>
                                                <PencilSimple size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={5} className="p-8 text-center text-[var(--muted)] text-sm">
                                    Loading assumptions…
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
